import { inject, Injectable } from '@angular/core';
import { QuoteInputModel, QuoteInputOptions } from '@lx/quote-tool/shared/types';
import { LoggerService } from '@lx/shared/logger';
import { getDateStringFromDate, getToday } from '@lx/shared/utils';
import { OnlineQuoteToolState } from '../store';

/**
 * Maps Online Quote Tool state to QuoteInputModel for API price calculation.
 *
 * This mapper is specific to online courses which have a simpler structure
 * compared to in-person courses (no accommodation, transfers, etc.).
 */
@Injectable({
  providedIn: 'root',
})
export class OnlineQuoteInputMapper {
  private readonly loggerService = inject(LoggerService);

  /**
   * Maps the online quote tool state to a quote input model for API consumption.
   *
   * @param state - The current state of the online quote tool
   * @returns QuoteInputModel for price calculation API
   */
  mapQuoteInput(state: Partial<OnlineQuoteToolState>): QuoteInputModel | null {
    if (!state || Object.keys(state).length === 0) {
      this.loggerService.error('Quote input state is empty - cannot map to quote input model');
      return null;
    }

    const { selectedCourse, selectedDuration, selectedStartDate, quoteId, residenceCountryCode, selectedSchool } = state;

    // Validate required fields for price calculation
    if (!selectedCourse?.code || !selectedDuration || !selectedStartDate) {
      this.loggerService.warn(
        `Missing required fields for quote input mapping - Course: ${!!selectedCourse?.code}, Duration: ${!!selectedDuration}, StartDate: ${!!selectedStartDate}`,
      );
      return null;
    }

    if (!selectedSchool) {
      this.loggerService.error('School code is required for quote input mapping');
      return null;
    }

    const options: QuoteInputOptions = {
      schoolCode: selectedSchool,
      quoteDate: getDateStringFromDate(getToday()),
      tuitionStartDate: selectedStartDate,
      residenceCountry: residenceCountryCode,
    };

    const products = [
      {
        code: selectedCourse.code,
        units: selectedDuration,
      },
    ];

    return {
      options,
      products,
      quoteId,
    };
  }
}
